import { useState } from 'react'
import {
  Modal,
  Select,
  Form,
  Space,
  Typography,
  Tag,
  Alert,
  Descriptions,
  Card,
  Switch,
  message,
  Button,
} from 'antd'
import { EyeOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { featureAssignmentsApi } from '@/api/feature-assignments'
import { FeatureValueEditor } from './FeatureValueEditor'
import { FeaturePreview } from './FeaturePreview'
import type { FeatureDefinition } from '@/types/feature-definition'
import type { PreviewFeatureResponse } from '@/api/feature-assignments'

const { Text } = Typography

// Helper to extract error message from API error response
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    const data = error.response.data
    if (data && typeof data === 'object' && 'error' in data) {
      const errorObj = data.error
      if (errorObj && typeof errorObj === 'object' && 'message' in errorObj) {
        const msg = errorObj.message
        if (typeof msg === 'string') {
          return msg
        }
      }
    }
  }
  return defaultMessage
}

interface FeatureAssignmentModalProps {
  hospitalId: string
  open: boolean
  onClose: () => void
  assignedFeatureIds?: Set<string>
}

export const FeatureAssignmentModal = ({
  hospitalId,
  open,
  onClose,
  assignedFeatureIds = new Set(),
}: FeatureAssignmentModalProps) => {
  const [form] = Form.useForm()
  const [selectedFeature, setSelectedFeature] = useState<FeatureDefinition | null>(null)
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [previewData, setPreviewData] = useState<PreviewFeatureResponse['data'] | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [validatedValues, setValidatedValues] = useState<{
    featureId: string
    enabled: boolean
    value: unknown
  } | null>(null)
  const queryClient = useQueryClient()

  // Fetch available features for this hospital
  const { data: availableFeaturesData, isLoading: loadingFeatures } = useQuery({
    queryKey: ['available-features', hospitalId],
    queryFn: () => featureAssignmentsApi.getAvailableFeatures(hospitalId),
    enabled: open,
  })

  const assignMutation = useMutation({
    mutationFn: (data: { featureId: string; enabled: boolean; value?: unknown }) =>
      featureAssignmentsApi.assignFeature(hospitalId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-assignments', hospitalId] })
      queryClient.invalidateQueries({ queryKey: ['hospital-features', hospitalId] })
      queryClient.invalidateQueries({ queryKey: ['hospital', hospitalId] })
      message.success('Feature assigned successfully')
      handleClose()
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, 'Failed to assign feature'))
    },
  })

  const handleClose = () => {
    form.resetFields()
    setSelectedFeature(null)
    setIsPreviewMode(false)
    setPreviewData(null)
    setValidatedValues(null)
    onClose()
  }

  const handleFeatureSelect = (featureId: string) => {
    const feature = availableFeaturesData?.data.find((f) => f.id === featureId)
    if (feature) {
      setSelectedFeature(feature)
      // Set default enabled state
      form.setFieldsValue({ enabled: true })
      // Reset preview when feature changes
      setIsPreviewMode(false)
      setPreviewData(null)
    }
  }

  const handlePreview = async () => {
    try {
      const values = await form.validateFields()

      if (!selectedFeature) {
        message.error('Please select a feature')
        return
      }

      // Parse JSON value if data type is json
      let finalValue = values.value
      if (selectedFeature.data_type === 'json' && typeof values.value === 'string') {
        try {
          finalValue = JSON.parse(values.value)
        } catch (_e) {
          message.error('Invalid JSON value')
          return
        }
      }

      setIsLoadingPreview(true)

      // Store validated values for later submission
      const submitData = {
        featureId: selectedFeature.id,
        enabled: values.enabled ?? true,
        value: finalValue,
      }
      setValidatedValues(submitData)

      const response = await featureAssignmentsApi.previewFeatureAssignment(hospitalId, submitData)

      setPreviewData(response.data)
      setIsPreviewMode(true)
    } catch (error) {
      console.error('Preview error:', error)
      message.error(getErrorMessage(error, 'Failed to generate preview'))
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handleBackToEdit = () => {
    setIsPreviewMode(false)
  }

  const handleSubmit = async () => {
    try {
      // If in preview mode, use the stored validated values
      if (isPreviewMode && validatedValues) {
        assignMutation.mutate(validatedValues)
        return
      }

      // Otherwise, validate form and submit
      const values = await form.validateFields()

      if (!selectedFeature) {
        message.error('Please select a feature')
        return
      }

      // Parse JSON value if data type is json
      let finalValue = values.value
      if (selectedFeature.data_type === 'json' && typeof values.value === 'string') {
        try {
          finalValue = JSON.parse(values.value)
        } catch (_e) {
          message.error('Invalid JSON value')
          return
        }
      }

      assignMutation.mutate({
        featureId: selectedFeature.id,
        enabled: values.enabled ?? true,
        value: finalValue,
      })
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  // Filter out already assigned features
  const availableFeatures = availableFeaturesData?.data.filter((f) => !assignedFeatureIds.has(f.id))

  return (
    <Modal
      title={isPreviewMode ? 'Preview Feature Assignment' : 'Assign Feature'}
      open={open}
      onCancel={handleClose}
      footer={
        isPreviewMode
          ? [
              <Button key="back" onClick={handleBackToEdit} icon={<EditOutlined />}>
                Back to Edit
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={assignMutation.isPending}
                onClick={handleSubmit}
              >
                Confirm Assignment
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button
                key="preview"
                type="default"
                icon={<EyeOutlined />}
                onClick={handlePreview}
                loading={isLoadingPreview}
                disabled={!selectedFeature}
              >
                Preview Changes
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleSubmit}
                loading={assignMutation.isPending}
                disabled={!selectedFeature}
              >
                Assign Feature
              </Button>,
            ]
      }
      width={isPreviewMode ? 900 : 700}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Show preview if in preview mode */}
        {isPreviewMode && previewData ? (
          <FeaturePreview preview={previewData} />
        ) : (
          <>
            {availableFeatures && availableFeatures.length === 0 && (
              <Alert
                type="info"
                message="All Available Features Assigned"
                description="This hospital has all available features assigned. Unassign features to add different ones."
                showIcon
              />
            )}

            <Form form={form} layout="vertical">
              <Form.Item
                name="featureId"
                label="Select Feature"
                rules={[{ required: true, message: 'Please select a feature' }]}
              >
                <Select
                  placeholder="Choose a feature to assign"
                  loading={loadingFeatures}
                  onChange={handleFeatureSelect}
                  showSearch
                  optionFilterProp="label"
                  options={availableFeatures?.map((feature) => ({
                    label: feature.name,
                    value: feature.id,
                    description: feature.description,
                  }))}
                  optionRender={(option) => (
                    <Space direction="vertical" size={0}>
                      <Text strong>{option.label}</Text>
                      {option.data.description && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {option.data.description}
                        </Text>
                      )}
                    </Space>
                  )}
                />
              </Form.Item>

              {selectedFeature && (
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Key">
                      <code>{selectedFeature.key}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Type">
                      <Tag color="blue">{selectedFeature.data_type}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Storage">
                      <Tag>{selectedFeature.storage_location}</Tag>
                      {selectedFeature.storage_path && (
                        <>
                          {' / '}
                          <code>{selectedFeature.storage_path}</code>
                        </>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}

              {selectedFeature && (
                <>
                  <Form.Item
                    name="enabled"
                    label="Enabled"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                  </Form.Item>

                  <FeatureValueEditor
                    feature={selectedFeature}
                    value={selectedFeature.default_value}
                  />
                </>
              )}
            </Form>
          </>
        )}
      </Space>
    </Modal>
  )
}
